import { numbersOf } from "./numbers.js";
import { normalizeText } from "./text.js";

/**
 * Editorial style rules calibrated on the OPULANCIA library (25 published carousels,
 * texts read by the stored analyses, measured 2026-10-10) and on Signature v0.2:
 * docs/EDITORIAL_CALIBRATION.md. Common rules first, then what each format adapts.
 * The ranges describe what was published: they are not targets proven to perform.
 * No Node dependency: the web app's Atelier imports this file.
 */

/** Formats of Signature v0.2 § 4.3 (F5, F6 and the « choice » variant are leads, from 1 or 2 examples). */
export type FormatId = "F1" | "F2" | "F3" | "F4" | "F5" | "F6" | "F1-choice";

export interface FormatProfile {
  id: FormatId;
  label: string;
  /** Editorial family of docs/EDITORIAL_FAMILIES_V1.md, when one fits. */
  family: "list" | "comparison" | "story" | null;
  /** Slides in the published examples (cover and end included). */
  slides: { min: number; max: number };
  /** Inner title, characters: warning above. */
  titleChars: number;
  /** Inner secondary text, characters: warning above (0: the format has none). */
  subChars: number;
  /** Inner slides may carry no text (photos speak). */
  textlessInner: boolean;
  /** What the format adapts, in plain words (shown to editors). */
  notes: string[];
  /** Library examples (deposit numbers), for reference only. */
  examples: number[];
}

/** Common rules: every format, unless its profile says otherwise. */
export const COMMON = {
  /** Cover: warning above (published covers: 8 to about 70 characters, 2–13 words). */
  hookChars: 70,
  hookWords: 13,
  /** Inner title default: warning above (published lists: ≤ 55). */
  titleChars: 60,
  /** Secondary text default: warning above (published explanations: up to 188). */
  subChars: 200,
  /** A hook that announces a count promises at least that many items (blocking below, warning above). */
  countMustMatch: true,
  /** At most this many different calls to action on the last slide (warning above 2, blocking at 3). */
  maxCtaActions: 2,
} as const;

export const FORMAT_PROFILES: Record<FormatId, FormatProfile> = {
  F1: {
    id: "F1",
    label: "Liste commentée",
    family: "list",
    slides: { min: 8, max: 12 },
    titleChars: 60,
    subChars: 200,
    textlessInner: false,
    notes: [
      "un élément par slide : titre court, explication dans le texte secondaire",
      "numérotation optionnelle (2 listes sur 7 sont numérotées)",
      "si l'accroche annonce un nombre, il est égal au nombre d'éléments",
    ],
    examples: [2, 3, 4, 6, 16, 18, 23],
  },
  F2: {
    id: "F2",
    label: "Comparaison",
    family: "comparison",
    slides: { min: 7, max: 12 },
    titleChars: 90,
    subChars: 120,
    textlessInner: true,
    notes: ["des paires (côte à côte ou dans une même scène) ; étiquettes répétées", "des slides intérieures peuvent être des photos sans texte"],
    examples: [1, 5, 8, 12, 20, 24],
  },
  F3: {
    id: "F3",
    label: "Galerie encadrée",
    family: null,
    slides: { min: 6, max: 8 },
    titleChars: 60,
    subChars: 0,
    textlessInner: true,
    notes: ["l'accroche annonce la chute ; les photos intérieures n'ont pas de texte"],
    examples: [9, 11, 21],
  },
  F4: {
    id: "F4",
    label: "Citations",
    family: null,
    slides: { min: 11, max: 12 },
    titleChars: 120,
    subChars: 40,
    textlessInner: false,
    notes: ["la citation est le titre (jusqu'à environ 100 caractères)", "attribution dans le texte secondaire ; chaque citation a une source"],
    examples: [15, 17, 22],
  },
  F5: {
    id: "F5",
    label: "Étiquettes-verdicts (piste)",
    family: null,
    slides: { min: 6, max: 11 },
    titleChars: 30,
    subChars: 0,
    textlessInner: false,
    notes: ["une étiquette courte par photo"],
    examples: [7, 10],
  },
  F6: {
    id: "F6",
    label: "Récit en étapes (piste)",
    family: "story",
    slides: { min: 8, max: 11 },
    titleChars: 60,
    subChars: 200,
    textlessInner: false,
    notes: ["une étape par slide, dans le temps ou l'espace", "les faits incertains restent au conditionnel (« aurait », « jusqu'à »)"],
    examples: [14, 25],
  },
  "F1-choice": {
    id: "F1-choice",
    label: "Liste « choix » (piste)",
    family: "list",
    slides: { min: 8, max: 14 },
    titleChars: 60,
    subChars: 60,
    textlessInner: false,
    notes: ["question de choix, puis des options numérotées"],
    examples: [19],
  },
};

/**
 * The count a hook announces: a number of at most 30 among its first three words, or after
 * « TOP » (« 7 BOUTONS… », « TOP 10 DES… »). Years, prices or durations later in the title
 * (« … D'ICI 2050 », « … EN 2 SECONDES ») are not counts.
 */
export function announcedCount(hook: string): number | null {
  const words = hook.trim().split(/\s+/);
  const top = /^top$/i.test(words[0] ?? "") ? words[1] : undefined;
  for (const word of top ? [top] : words.slice(0, 3)) {
    const n = numbersOf(word)[0];
    if (n !== undefined && /^\d+$/.test(n) && Number(n) > 0 && Number(n) <= 30) return Number(n);
  }
  return null;
}

/** Words of a hook as a reader counts them (an amount like « 100 000 € » is one word). */
export function wordCount(text: string): number {
  const joined = text.replace(/(\d)[\s  ](?=\d{3}(?!\d))/g, "$1");
  return joined.trim().split(/\s+/).filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
}

export type CtaAction = "follow" | "share" | "save" | "comment";

/** Calls to action a closing text makes (several can be combined, as observed in the library). */
export function ctaActions(text: string): CtaAction[] {
  const t = ` ${normalizeText(text)} `;
  const found = new Set<CtaAction>();
  if (/ (abonne|abonnes|abonnez|suis nous|suis opulancia|suivre) /.test(t)) found.add("follow");
  if (/ (partage|partager|envoie|envoyer|identifie|tague) /.test(t)) found.add("share");
  if (/ (enregistre|enregistrer|garde le|sauvegarde) /.test(t)) found.add("save");
  if (/ (commentaire|commentaires|commente|dis le|reponds|reponds en) /.test(t) || /\?\s*$/.test(text.trim())) found.add("comment");
  return [...found];
}

export interface StyleSlide {
  title: string;
  sub?: string;
}

export interface StyleInput {
  format: FormatId;
  hook: string;
  /** Inner slides, in reading order (cover and last slide excluded). */
  items: StyleSlide[];
  /** Text of the last slide (the call to action). */
  end: string;
  /** What the carousel is for, when known. */
  objective?: CtaAction;
}

export interface StyleIssue {
  code: string;
  level: "warning" | "blocking";
  message: string;
}

/** The calibrated rules (common, then the format's adaptations). */
export function styleIssues(input: StyleInput): StyleIssue[] {
  const profile = FORMAT_PROFILES[input.format];
  const issues: StyleIssue[] = [];
  const add = (code: string, level: StyleIssue["level"], message: string) => issues.push({ code, level, message });

  // hook
  const hookChars = input.hook.length;
  const hookWords = wordCount(input.hook);
  if (hookChars > COMMON.hookChars || hookWords > COMMON.hookWords) {
    add("HOOK_LONG", "warning", `accroche de ${hookChars} caractères et ${hookWords} mots (couvertures publiées : jusqu'à environ 70 caractères et 13 mots)`);
  }
  const announced = announcedCount(input.hook);
  const actions = ctaActions(input.end);
  // a last slide that asks for nothing is the last item (observed: a 10th quote closing the carousel)
  const counted = input.items.length + (actions.length ? 0 : 1);
  // fewer items than announced breaks the promise; one more reads as a bonus (observed once)
  if (announced !== null && counted < announced) {
    add("HOOK_COUNT_MISMATCH", "blocking", `l'accroche annonce ${announced} éléments, le carrousel n'en contient que ${counted}`);
  } else if (announced !== null && counted > announced) {
    add("HOOK_COUNT_BONUS", "warning", `l'accroche annonce ${announced} éléments, le carrousel en contient ${counted} : bonus voulu ?`);
  }

  // density
  input.items.forEach((item, i) => {
    const n = i + 2;
    if (!item.title.trim() && !profile.textlessInner && !(item.sub ?? "").trim()) add("TEXTLESS_SLIDE", "warning", `slide ${n} sans texte (le format ${profile.label} en porte d'habitude)`);
    if (item.title.length > profile.titleChars) add("TITLE_LONG", "warning", `slide ${n} : titre de ${item.title.length} caractères (repère ${profile.label} : ${profile.titleChars})`);
    const sub = item.sub ?? "";
    if (profile.subChars === 0 && sub.trim()) add("SUB_UNEXPECTED", "warning", `slide ${n} : texte secondaire inhabituel pour le format ${profile.label}`);
    else if (profile.subChars && sub.length > profile.subChars) add("SUB_LONG", "warning", `slide ${n} : texte secondaire de ${sub.length} caractères (repère ${profile.label} : ${profile.subChars})`);
    if (input.format === "F4" && item.title.trim() && !/(^|\s)[—–-]\s*\S/.test(sub)) add("QUOTE_UNATTRIBUTED", "blocking", `slide ${n} : citation sans attribution`);
  });

  // length of the carousel
  const total = input.items.length + 2;
  if (total < profile.slides.min || total > profile.slides.max) {
    add("SLIDE_COUNT", "warning", `${total} slides ; les exemples publiés du format ${profile.label} en ont de ${profile.slides.min} à ${profile.slides.max} (repère, pas une règle de performance)`);
  }

  // call to action
  if (!actions.length) add("CTA_MISSING", "warning", "la dernière slide ne demande aucune action (abonnement, partage, enregistrement ou commentaire)");
  if (actions.length >= 3) add("CTA_STACKED", "blocking", `la fin demande ${actions.length} actions à la fois (${actions.join(", ")}) : un objectif principal`);
  else if (actions.length === 2) add("CTA_TWO_ACTIONS", "warning", `la fin demande deux actions (${actions.join(", ")}) ; vérifier que l'objectif principal reste clair`);
  if (input.objective && actions.length && !actions.includes(input.objective)) add("CTA_OFF_OBJECTIVE", "warning", `la fin ne sert pas l'objectif choisi (${input.objective})`);

  // tone: the library speaks « tu »; « vous » mixed in is flagged, not forbidden
  const all = [input.hook, ...input.items.flatMap((s) => [s.title, s.sub ?? ""]), input.end].join(" ");
  const tu = / (tu|ton|ta|tes|toi|t) /.test(` ${normalizeText(all)} `);
  const vous = / (vous|votre|vos) /.test(` ${normalizeText(all)} `);
  if (tu && vous) add("TONE_MIXED", "warning", "le texte mélange « tu » et « vous » (la bibliothèque tutoie)");
  return issues;
}
